/**
 * app/viewModels/index.ts — MER Algo Footprints v8.2.0
 *
 * Workspace-specific view-model builders.
 *
 * Each function transforms engine outputs (SymbolContext, SymbolThesis,
 * LiveGammaSnapshot) into a flat, display-oriented structure.
 *
 * ── Design rules ──────────────────────────────────────────────────────────────
 * 1. No JSX — pure TypeScript transformations
 * 2. All fields default to null/undefined — never throw on missing data
 * 3. Display logic only — no engine re-computation
 * 4. Replay-safe — deterministic from snapshot inputs
 */

import type { SymbolContext } from '../../domains/market/services/buildSymbolContext';
import type { SymbolThesis }  from '../../domains/ai/thesis/thesisEngine';

// ── Shared helpers ────────────────────────────────────────────────────────────

function _price(n: number | null | undefined): string {
  if (n == null) return '—';
  return n.toFixed(2);
}


// ── Command Center view-model ─────────────────────────────────────────────────

export interface CommandCenterVM {
  // State
  symbol:          string;
  price:           string;
  regime:          string;
  directionalBias: string;
  confidence:      string;
  // Gamma
  gammaPosture:    string | null;
  callWall:        string | null;
  putWall:         string | null;
  flipZone:        string | null;
  // Trap
  trapClassification: string | null;
  trapRisk:           string | null;
  trapStrength:       string | null;
  // Thesis
  overallBias:     string | null;
  actionability:   number | null;
  primarySignal:   string | null;   // derived from overallBias + marketState
  // State
  actionabilityState: 'ready' | 'watch' | 'avoid';
}

export function buildCommandCenterVM(
  symbol:  string,
  ctx:     SymbolContext | null,
  thesis:  SymbolThesis  | null,
): CommandCenterVM {
  const price = ctx?.curPrice != null ? _price(ctx.curPrice) : '—';
  const regime = ctx?.regime?.label ?? '—';
  const conf   = ctx?.regime?.confidence != null ? `${ctx.regime.confidence}%` : '—';

  const current = thesis?.current;
  const st       = ctx?.stateTransition;

  let state: CommandCenterVM['actionabilityState'] = 'avoid';
  if (st?.actionability === 'actionable') state = 'ready';
  else if (st?.actionability === 'pending') state = 'watch';

  const trap = ctx?.trapInterpretation;

  return {
    symbol,
    price,
    regime,
    directionalBias: current?.overallBias ?? ctx?.regime?.posture ?? '—',
    confidence:      conf,
    gammaPosture:    ctx?.gamma?.regime ?? null,
    callWall:        ctx?.gamma?.callWall != null ? _price(ctx.gamma.callWall) : null,
    putWall:         ctx?.gamma?.putWall  != null ? _price(ctx.gamma.putWall)  : null,
    flipZone:        ctx?.gamma?.gammaFlip != null ? _price(ctx.gamma.gammaFlip) : null,
    trapClassification: trap?.trapClassification ?? null,
    trapRisk:           trap?.signals.trapRisk    ?? null,
    trapStrength:       trap?.trapStrength        ?? null,
    overallBias:     current?.overallBias         ?? null,
    actionability:   current?.actionability       ?? null,
    primarySignal:   current?.bestSetupType        ?? null,
    actionabilityState: state,
  };
}

// ── Structure view-model ──────────────────────────────────────────────────────

export interface StructureVM {
  symbol:       string;
  // Gamma
  regime:       string | null;
  posture:      string | null;
  callWall:     string | null;
  putWall:      string | null;
  flipZone:     string | null;
  concentration: string | null;
  // Liquidity zones
  topZones: Array<{ price: string; label: string; type: string; sig: string }>;
  // SMC
  smcBias:       string | null;
  lastBOS:       string | null;
  lastCHOCH:     string | null;
  sweepReverse:  boolean;
}

export function buildStructureVM(
  symbol: string,
  ctx:    SymbolContext | null,
): StructureVM {
  const g  = ctx?.gamma;
  const smc = ctx?.smc;
  const zones = (ctx?.zones ?? []).slice(0, 6);

  return {
    symbol,
    regime:       g?.regime  ?? null,
    posture:      g?.regime  ?? null,
    callWall:     g?.callWall  != null ? _price(g.callWall)  : null,
    putWall:      g?.putWall   != null ? _price(g.putWall)   : null,
    flipZone:     g?.gammaFlip != null ? _price(g.gammaFlip) : null,
    concentration: null, // populated from v2 layer when available
    topZones: zones.map(z => ({
      price: _price(z.price),
      label: z.label,
      type:  z.type,
      sig:   z.sig,
    })),
    smcBias:      smc?.bias ?? null,
    lastBOS:      smc?.lastBOS ? `${smc.lastBOS.dir} @ ${_price(smc.lastBOS.price)}` : null,
    lastCHOCH:    smc?.lastCHOCH ? `${smc.lastCHOCH.dir} @ ${_price(smc.lastCHOCH.price)}` : null,
    sweepReverse: smc?.sweepAndReverse ?? false,
  };
}

// ── Signals view-model ────────────────────────────────────────────────────────

export interface SignalsVM {
  symbol:          string;
  // Thesis
  overallBias:     string | null;
  conviction:      number | null;
  actionability:   number | null;
  shouldTradeNow:  boolean;
  urgency:         string | null;
  primarySignal:   string | null;
  invalidation:    string | null;
  // Regime
  regimeDominant:  string | null;
  regimeConf:      number | null;
  breakoutBias:    boolean;
  reversalBias:    boolean;
  // Trap
  trapClass:       string | null;
  trapRisk:        string | null;
  trapStrength:    string | null;
  trapSide:        string | null;
  whipsawRisk:     string | null;
  // Vol context
  volRegime:       string | null;
  volImpact:       string | null;
}

/**
 * Optional adaptive calibration profile — when provided and active adaptation is ON,
 * applies the global confidence calibration modifier to conviction.
 * When null or adaptation OFF → pure pass-through, identical to advisory behavior.
 */
/**
 * Build the Signals view-model.
 * overrideConviction — when provided (by canonical adaptation layer via useAdaptiveSignalsVM),
 * replaces the raw thesis conviction. This is the ONLY way adapted conviction enters the VM.
 * Callers that do not participate in active adaptation omit it (advisory passthrough).
 */
export function buildSignalsVM(
  symbol:             string,
  ctx:                SymbolContext | null,
  thesis:             SymbolThesis  | null,
  overrideConviction: number | null = null,
): SignalsVM {
  const current = thesis?.current;
  const trap    = ctx?.trapInterpretation;
  const regime  = ctx?.regime;

  return {
    symbol,
    overallBias:    current?.overallBias    ?? null,
    conviction:     overrideConviction ?? (current?.conviction ?? null),
    actionability:  current?.actionability  ?? null,
    shouldTradeNow: current?.shouldTradeNow ?? false,
    urgency:        current?.urgency        ?? null,
    primarySignal:  current?.bestSetupType  ?? null,
    invalidation:   current?.invalidation   ?? null,
    regimeDominant: regime?.dominant        ?? null,
    regimeConf:     regime?.confidence      ?? null,
    breakoutBias:   regime?.dominant === 'breakout',
    reversalBias:   regime?.dominant === 'meanReversion',
    trapClass:      trap?.trapClassification ?? null,
    trapRisk:       trap?.signals.trapRisk   ?? null,
    trapStrength:   trap?.trapStrength       ?? null,
    trapSide:       trap?.trapSide           ?? null,
    whipsawRisk:    trap?.signals.whipsawRisk ?? null,
    volRegime:      (trap?.volatilityContext as any)?.regime ?? null,
    volImpact:      (trap?.volatilityContext as any)?.impact ?? null,
  };
}

// ── Replay view-model ─────────────────────────────────────────────────────────

export interface ReplayVM {
  symbol:       string;
  thesisCount:  number;
  latestBias:   string | null;
  latestConviction: number | null;
  regimeLabel:  string | null;
  hasTraps:     boolean;
  trapSummary:  string | null;
}

export function buildReplayVM(
  symbol: string,
  ctx:    SymbolContext | null,
  thesis: SymbolThesis  | null,
): ReplayVM {
  const trap = ctx?.trapInterpretation;
  return {
    symbol,
    thesisCount:      thesis?.history?.length ?? 0,
    latestBias:       thesis?.current?.overallBias ?? null,
    latestConviction: thesis?.current?.conviction  ?? null,
    regimeLabel:      ctx?.regime?.label ?? null,
    hasTraps:         (ctx?.traps?.length ?? 0) > 0,
    trapSummary:      trap?.trapClassification !== 'none' ? trap?.trapClassification ?? null : null,
  };
}

// ── Journal view-model ────────────────────────────────────────────────────────

export interface JournalVM {
  symbol:       string;
  entryCount:   number;
  winRate:      string | null;
  recentSetup:  string | null;
}

export function buildJournalVM(
  symbol:  string,
): JournalVM {
  return {
    symbol,
    entryCount:  0,  // populated from journal store
    winRate:     null,
    recentSetup: null,
  };
}
